import { Extension } from '@tiptap/react'
import { type Editor, ReactRenderer } from '@tiptap/react'
import Suggestion, { type SuggestionOptions, type SuggestionProps } from '@tiptap/suggestion'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import { SlashMenu, type SlashMenuRef } from '../components/SlashMenu'
import { ImageUpload } from '../components/ImageUpload'
import { handleImageFile, type ImageHandlerOptions } from '../lib/imageHandler'
import { track, AnalyticsEvents } from '../lib/mixpanel'

export interface SlashCommandItem {
  id: string
  label: string
  shortcut?: string
  icon: string // lucide icon name
  command: (editor: Editor) => void
}

function getItems(query: string, imageHandler?: ImageHandlerOptions): SlashCommandItem[] {
  const items: SlashCommandItem[] = [
    {
      id: 'text',
      label: 'Text',
      icon: 'Type',
      command: (editor) => {
        editor.chain().focus().setParagraph().run()
      },
    },
    {
      id: 'heading1',
      label: 'Heading 1',
      shortcut: '#',
      icon: 'Heading1',
      command: (editor) => {
        editor.chain().focus().setHeading({ level: 1 }).run()
      },
    },
    {
      id: 'heading2',
      label: 'Heading 2',
      shortcut: '##',
      icon: 'Heading2',
      command: (editor) => {
        editor.chain().focus().setHeading({ level: 2 }).run()
      },
    },
    {
      id: 'heading3',
      label: 'Heading 3',
      shortcut: '###',
      icon: 'Heading3',
      command: (editor) => {
        editor.chain().focus().setHeading({ level: 3 }).run()
      },
    },
    {
      id: 'bulletList',
      label: 'Bulleted List',
      shortcut: '-',
      icon: 'List',
      command: (editor) => {
        editor.chain().focus().toggleBulletList().run()
      },
    },
    {
      id: 'orderedList',
      label: 'Numbered List',
      shortcut: '1.',
      icon: 'ListOrdered',
      command: (editor) => {
        editor.chain().focus().toggleOrderedList().run()
      },
    },
    {
      id: 'quote',
      label: 'Quote',
      shortcut: '>',
      icon: 'Quote',
      command: (editor) => {
        editor.chain().focus().toggleBlockquote().run()
      },
    },
    {
      id: 'codeBlock',
      label: 'Code Block',
      shortcut: '```',
      icon: 'Code',
      command: (editor) => {
        editor.chain().focus().toggleCodeBlock().run()
      },
    },
    {
      id: 'divider',
      label: 'Divider',
      shortcut: '---',
      icon: 'Minus',
      command: (editor) => {
        editor.chain().focus().setHorizontalRule().run()
      },
    },
    {
      id: 'taskList',
      label: 'To-do List',
      shortcut: '[]',
      icon: 'CheckSquare',
      command: (editor) => {
        editor.chain().focus().toggleTaskList().run()
      },
    },
    {
      id: 'image',
      label: 'Image',
      icon: 'ImagePlus',
      command: (editor) => {
        // Show image upload overlay
        const container = document.createElement('div')
        container.id = 'image-upload-overlay'
        document.body.appendChild(container)

        const cleanup = () => {
          renderer.destroy()
          container.remove()
        }

        const renderer = new ReactRenderer(ImageUpload, {
          props: {
            onFileSelected: (file: File) => {
              if (imageHandler) {
                handleImageFile(editor.view, file, imageHandler)
              }
              cleanup()
              editor.commands.focus()
            },
            onClose: () => {
              cleanup()
              editor.commands.focus()
            },
          },
          editor,
        })

        container.appendChild(renderer.element)
      },
    },
  ]

  if (!query) return items

  const lower = query.toLowerCase()
  return items.filter((item) =>
    item.label.toLowerCase().includes(lower) ||
    item.id.toLowerCase().includes(lower)
  )
}

export interface SlashCommandOptions {
  suggestion: Partial<SuggestionOptions<SlashCommandItem>>
  imageHandler?: ImageHandlerOptions
}

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: 'slashCommand',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        startOfLine: false,
        command: ({ editor, range, props: item }) => {
          // Delete the slash trigger text
          editor.chain().focus().deleteRange(range).run()
          // Execute the item's command
          item.command(editor)
          // Track slash command usage
          track(AnalyticsEvents.SLASH_COMMAND_USED, { command: item.id })
        },
      },
      imageHandler: undefined,
    }
  },

  addProseMirrorPlugins() {
    const imageHandler = this.options.imageHandler

    return [
      Suggestion<SlashCommandItem>({
        editor: this.editor,
        ...this.options.suggestion,
        items: ({ query }: { query: string }) => getItems(query, imageHandler),
        render: () => {
          let component: ReactRenderer<SlashMenuRef> | null = null
          let popup: TippyInstance | null = null

          return {
            onStart: (props: SuggestionProps<SlashCommandItem>) => {
              component = new ReactRenderer(SlashMenu, {
                props,
                editor: props.editor,
              })

              if (!props.clientRect) return

              popup = tippy(document.body, {
                getReferenceClientRect: props.clientRect as () => DOMRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                theme: 'slash-menu',
                maxWidth: 'none',
                offset: [0, 4],
                popperOptions: {
                  modifiers: [
                    { name: 'flip', enabled: true },
                    { name: 'preventOverflow', enabled: true, options: { padding: 8, altAxis: true, tether: false } }
                  ],
                },
              }) as unknown as TippyInstance
            },

            onUpdate: (props: SuggestionProps<SlashCommandItem>) => {
              component?.updateProps(props)

              if (popup && props.clientRect) {
                popup.setProps({
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                })
              }
            },

            onKeyDown: (props: { event: KeyboardEvent }) => {
              if (props.event.key === 'Escape') {
                popup?.hide()
                return true
              }

              return component?.ref?.onKeyDown(props.event) ?? false
            },

            onExit: () => {
              popup?.destroy()
              component?.destroy()
              popup = null
              component = null
            },
          }
        },
      }),
    ]
  },
})
